import type { ITenant } from '@/shared/api/types/tenant-types'
import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { useTenantsQuery } from '@/entities/tenant'
import { TenantFormDrawer } from '@/features/tenant/tenant-form-drawer'
import { TenantListTable } from '@/features/tenant/tenant-list-table'
import { TenantSearchBar } from '@/features/tenant/tenant-search-bar'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { TenantDetailPage } from './detail'

type TenantStatusFilter = 'active' | 'inactive' | 'all'

function statusToIsActive(filter: TenantStatusFilter): boolean | undefined {
  if (filter === 'active') return true
  if (filter === 'inactive') return false
  return undefined
}

export default function TenantPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const { isTenantAdmin, tenantId } = useAdminScope()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<TenantStatusFilter>('active')

  const isActiveParam = statusToIsActive(statusFilter)
  const { data: tenantsResponse, isLoading } = useTenantsQuery(
    {
      page,
      pageSize,
      ...(isActiveParam !== undefined ? { isActive: isActiveParam } : {}),
    },
    { enabled: !isTenantAdmin },
  )

  const tenants = useMemo(() => {
    const items = tenantsResponse?.data ?? []
    const keyword = searchTerm.trim().toLowerCase()
    if (!keyword) {
      return items
    }
    return items.filter(
      (tenant: ITenant) =>
        tenant.name.toLowerCase().includes(keyword) ||
        tenant.slug.toLowerCase().includes(keyword) ||
        (tenant.description ?? '').toLowerCase().includes(keyword) ||
        (tenant.contactEmail ?? '').toLowerCase().includes(keyword) ||
        (tenant.website ?? '').toLowerCase().includes(keyword),
    )
  }, [tenantsResponse?.data, searchTerm])

  const total = searchTerm.trim() ? tenants.length : (tenantsResponse?.total ?? 0)
  const activeFilterCount = statusFilter === 'active' ? 0 : 1

  function handleSearch(term: string) {
    setSearchTerm(term)
    setPage(1)
  }

  function handlePageChange(newPage: number, newPageSize: number) {
    setPage(newPage)
    setPageSize(newPageSize)
  }

  function handleCreate() {
    setDrawerOpen(true)
  }

  function handleView(tenant: ITenant) {
    navigate({
      to: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId: tenant.id })) as never,
    }) as never
  }

  function handleDrawerClose() {
    setDrawerOpen(false)
  }

  if (isTenantAdmin) {
    return <TenantDetailPage tenantId={tenantId} />
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('tenant.title')}
        breadcrumbs={[
          { label: t('sidebar.dashboard'), path: scopedAdminPath('/dashboard') },
          { label: t('sidebar.tenants') },
        ]}
        actions={
          <Button onClick={handleCreate}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('tenant.create')}
          </Button>
        }
      />

      <DataTableToolbar>
        <DataTableFilterField
          label={t('common.search', { defaultValue: 'Search' })}
          className="min-w-[240px]"
        >
          <TenantSearchBar onSearch={handleSearch} className="w-64" />
        </DataTableFilterField>
        <FilterPopover
          title={t('common.filter')}
          resetLabel={t('common.reset')}
          activeCount={activeFilterCount}
          onReset={() => {
            setStatusFilter('active')
            setPage(1)
          }}
        >
          <DataTableFilterField label={t('common.status')}>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as TenantStatusFilter)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                <SelectItem value="active">
                  {t('common.active', { defaultValue: 'Active' })}
                </SelectItem>
                <SelectItem value="inactive">
                  {t('common.inactive', { defaultValue: 'Inactive' })}
                </SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>
        </FilterPopover>
      </DataTableToolbar>

      <TenantListTable
        tenants={tenants}
        loading={isLoading}
        pagination={{ current: page, total, pageSize }}
        onPageChange={handlePageChange}
        onView={handleView}
      />

      <TenantFormDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        onSuccess={handleDrawerClose}
      />
    </PageContainer>
  )
}
